import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { TicketFollowerResponse, AddTicketFollowerRequest, BulkAddTicketFollowersRequest } from '@troia-v3/types';
import { ticketFollowersService } from '../services/ticketFollowersService';

export function useTicketFollowers(ticketId: string | undefined) {
  return useQuery<TicketFollowerResponse[]>({
    queryKey: ['ticket-followers', ticketId],
    queryFn: () => ticketFollowersService.getByTicketId(ticketId!),
    enabled: !!ticketId,
    staleTime: 30 * 1000,
  });
}

export function useAddTicketFollower() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddTicketFollowerRequest) =>
      ticketFollowersService.addFollower(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-followers', variables.ticketId] });
    },
  });
}

export function useAddTicketFollowersBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkAddTicketFollowersRequest) =>
      ticketFollowersService.addFollowersBulk(data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-followers', variables.ticketId] });
    },
  });
}

export function useRemoveTicketFollower() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, followerType, followerId }: { ticketId: string; followerType: string; followerId: string }) =>
      ticketFollowersService.removeFollower(ticketId, followerType, followerId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-followers', variables.ticketId] });
    },
  });
}
